/* eslint-disable react/no-unescaped-entities */
// ─────────────────────────────────────────────────────────────
// BuddiesHR — Case study data.
// One object per customer, consumed by components/caseStudies/sections.
// Copy CS_ROO and change the strings to add a new story.
// `challenge.body`, `challenge.after` and `resultsSection.body` hold the
// narrative prose, so they carry markup (bold, product links).
// ─────────────────────────────────────────────────────────────
import Link from 'next/link';
import { CsInkLink } from '@buddieshr/components/caseStudies/sections';

export const CS_ROO = {
  slug: 'roo',
  company: 'Roo',
  logo: '/img/case-studies/roovet.png',
  what: 'Connects Veterinary and Tech relief with animal hospitals at the click of a button.',
  site: 'http://roo.vet/',
  siteLabel: 'roo.vet',
  linkedin: 'https://www.linkedin.com/company/roovets/',
  since: 'October 2024',
  readTime: '5 min read',
  industry: 'Veterinary marketplace',
  title: ['How Roo scaled recognition and stopped', 'tallying kudos by hand'],
  dek: 'Roo has been using BuddiesHR apps since October 2024 to scale their recognition program and reduce time spent on manual processes.',
  person: {
    name: 'Sammie Zettler',
    role: 'Senior People Partner at Roo',
    linkedin: 'https://www.linkedin.com/in/samanthazettler/',
    photo: '/img/case-studies/sammie_zettler.jpeg',
    initials: 'SZ',
  },
  facts: [
    ['100%', 'remote'],
    ['23', 'US states, plus Canada & Mexico'],
    ['40 → 120+', 'employees in 12 months'],
  ],
  apps: ['clappy', 'pulsy', 'billy', 'stany'],
  takeaways: [
    ['Implementation was super easy.', 'Everyone was able to use the apps very quicly.'],
    ['Employees love it.', ''],
    ['Better company morale.', 'The main benefit.'],
    ['Less time spent on manual processes.', 'HR can focus on other tasks.'],
  ],
  beforeAfter: {
    before: [
      'Points for every kudo tallied up by hand',
      'A spreadsheet filled in at the end of each day',
      'A monthly raffle with one lucky winner',
      'Recognition that stopped scaling past ~40 people',
    ],
    after: [
      'Every clap counts toward points automatically',
      'Points redeemable for real rewards',
      'Everyone can enjoy it, not just one winner',
      'Kudos, birthdays, eNPS and standups all automated',
    ],
  },
  criteria: ['Connected with Slack', 'Automated', 'Easy to use', 'Reporting'],
  foundUs: 'Google Search',
  whyBuy: [
    'Functionality was exactly what we wanted',
    'Cost Efficient',
    'Supporting other start-ups.',
  ],
  results: [
    'Boost of usage',
    'Boost of team morale',
    'Free up time for HRs',
    'Automated kudos, birthdays, eNPS survey and standups',
  ],
  challenge: {
    heading: 'A kudos channel that outgrew its spreadsheet',
    body: [
      <>
        Roo runs a Slack channel called <strong style={{ color: 'var(--bh-ink)' }}>#roohoo</strong>.
        “Our company is Roo, so roo and woohoo = roohoo :) We like our puns.” It is the{' '}
        <Link href="/kudos-recognition?ref=roo-case-study">Kudos channel</Link> where teammates give
        each other shoutouts.
      </>,
      <>
        At around 40 people, the People team tallied up the points for each kudo by hand and ran a
        monthly raffle based on those points. “When I was manually having to tally and enter people
        into a spreadsheet at the end of each day, taking up more time than it needed!”
      </>,
    ],
    after: (
      <>
        The trigger, in Sammie’s words: “Needing to focus my efforts on more important things than
        the manual process that was in place.”
      </>
    ),
  },
  resultsSection: {
    heading: 'Months in, and still running daily',
    body: [
      <>
        “Yes! We have been actively using BuddiesHR for months now and it has been great.”
        Recognition is automated, and the team reached for more of the suite:{' '}
        <CsInkLink href="/employee-survey?ref=roo-case-study">Pulsy</CsInkLink> for surveys,{' '}
        <CsInkLink href="/birthday-bot?ref=roo-case-study">Billy</CsInkLink> for birthdays,{' '}
        <CsInkLink href="/standup-bot?ref=roo-case-study">Stany</CsInkLink> for standups.
      </>,
      <>
        On the HR side: “Less time spent on manual processes. Better company morale.” And from the
        rest of the company: “Employees love it!”
      </>,
    ],
  },
  quotes: {
    challenge: {
      text: "But as we grew to nearly 100 employees 😱, this manual process wasn't sustainable. We wanted something more fun and scalable: something everyone could enjoy, not just one lucky winner each month.",
      highlight: "this manual process wasn't sustainable",
    },
    result: {
      text: 'It is no longer a manual process, the team is using it daily.',
      highlight: 'the team is using it daily',
    },
  },
  interview: [
    [
      'The challenge',
      [
        [
          'What issues were you trying to fix when you found BuddiesHR?',
          'We have a #roohoo [our company is Roo, so roo and woohoo = roohoo :) We like our puns ] channel in Slack. This is our Kudos channel where teammates can give each other shoutouts. Back when we were around 40 people, we used to manually tally up points for each kudo and do a monthly raffle based on those points.\n\nSo now we’ve gamified it! Every clap counts toward points you can actually redeem for rewards.\n\nNot to mention, one of our core values is "Have Fun" so this easily integrated into that.',
        ],
        [
          'How did you notice these issues?',
          'When I was manually having to tally and enter people into a spreadsheet at the end of each day, taking up more time than it needed!',
        ],
        ['What impact did it have on the business?', 'Better employee morale!'],
        [
          'What was the trigger that made you search for a solution?',
          'Needing to focus my efforts on more important things than the manual process that was in place.',
        ],
      ],
    ],
    [
      'Solution and implementation',
      [
        [
          'To fix the issues listed above, what were you looking for? What were the criteria?',
          '#1 Connected with Slack\n#2 Automated\n#3 Easy to use\n#4 Reporting',
        ],
        ['How did you find BuddiesHR?', 'Google Search'],
        [
          'The main reason that made you purchase?',
          '#1 Functionality was exactly what we wanted\n#2 Cost Efficient\n#3 Supporting other start-ups.',
        ],
      ],
    ],
    [
      'Results',
      [
        [
          'Did BuddiesHR fix the problem described above? How?',
          'Yes! We have been actively using BuddiesHR for months now and it has been great.\n\nIt is no longer a manual process, the team is using it daily.\n\nWe have even been using other features like Pulsy, Billy, and Stany that have added automation to our slack channels.',
        ],
        ['How has the business changed after using BuddiesHR?', 'More efficient, better morale.'],
        [
          'How was the ease of implementation? (how did it make their life easier?)',
          'Super easy! Everyone was able to adapt to Buddies very quickly.',
        ],
        [
          'What impacts did it have on the HR team?',
          'Less time spent on manual processes. Better company morale.',
        ],
        ['Did you receive feedback from the employees?', 'Employees love it!'],
      ],
    ],
  ],
  closing: [
    'Roo’s journey with BuddiesHR highlights how the right tools can make a real difference: not just for HR teams, but for the entire company culture.',
    'By automating time-consuming recognition processes and making them fun and engaging, Roo has successfully scaled their kudos program in a way that’s both sustainable and aligned with their values.',
    'As Roo continues to grow, BuddiesHR is proud to support them in building a more connected, recognized, and joyful workplace.',
  ],
};

export const CS_SCHED = {
  slug: 'sched',
  company: 'Sched',
  logo: '/img/case-studies/sched-logo.svg',
  what: 'A flexible and easy way for you to plan better events. Forget confusing spreadsheets. Simplify and centralize your events now.',
  site: 'http://sched.com/',
  siteLabel: 'sched.com',
  linkedin: 'https://www.linkedin.com/company/sched/',
  since: 'July 2024',
  readTime: '5 min read',
  industry: 'Event scheduling software',
  title: ['How Sched celebrates wins systematically,', 'with no HR team at all'],
  dek: 'Sched has been using BuddiesHR apps since July 2024 to automate celebrations and build the company culture they dreamed of.',
  person: {
    name: 'Marvin McTaw',
    role: 'CEO at Sched.com',
    linkedin: 'https://www.linkedin.com/in/marvinmctaw/',
    photo: '/img/case-studies/marvin.png',
    initials: 'MM',
  },
  facts: [
    ['100%', 'remote'],
    ['5 regions', 'USA, Canada, UK, Europe and Asia'],
    ['No HR team', 'yet, 32 employees so far'],
  ],
  apps: ['billy', 'clappy', 'alfy', 'pulsy', 'linky', 'stany'],
  takeaways: [
    ['Value for purchase is top notch.', ''],
    [
      'Lightened some of the administrative hurdle',
      'by automating things especially while not having an HR team.',
    ],
    ['They love the celebrations and the Kudos.', 'Employee feedback is positive.'],
    ['Gamification and visibility', 'helped create stickiness and good habits.'],
  ],
  beforeAfter: {
    before: [
      'Birthdays and work anniversaries handled manually',
      'Culture work landing on the CEO’s calendar',
      'More and more time spent on company culture things',
      'No HR team to hand any of it to',
    ],
    after: [
      'Celebrations run automatically',
      'Wins celebrated systematically',
      'Kudos and gamification building good habits',
      'Delegatable, so it does not sit with one person',
    ],
  },
  criteria: ['Connected with Slack', 'Automated', 'Delegatable'],
  foundUs: null,
  whyBuy: ['Functionality was exactly what we wanted', 'Cost efficient'],
  results: [
    'Culture is more positive',
    'Wins are celebrated systematically',
    'Free up time',
    'Appreciation for teamwork is more visible',
  ],
  challenge: {
    heading: 'Culture by intention, with no one to hand it to',
    body: [
      <>
        Sched is fully remote, distributed between the USA, Canada, the UK, Europe and Asia, with{' '}
        <strong style={{ color: 'var(--bh-ink)' }}>32 employees and no HR team</strong>. Culture work
        sat with the CEO.
      </>,
      <>
        “We were spending a lot of time doing things manually that could be automated and now
        BuddiesHR does that for us.” The trigger: “We were spending more and more time on company
        culture things like birthday and work anniversaries for example.”
      </>,
    ],
    after: (
      <>
        The goal was never only less admin. “We wanted to reduce manual work we were already doing,
        increase communication and accountability, etc.”
      </>
    ),
  },
  teamPhoto: {
    src: '/img/case-studies/sched_team.png',
    alt: 'The Sched team on an offsite, arms in the air',
    caption: 'Sched team',
  },
  resultsSection: {
    heading: 'A culture that runs without a culture team',
    body: [
      <>
        Six apps now run in their Slack:{' '}
        <CsInkLink href="/birthday-bot?ref=sched-case-study">Billy Birthday</CsInkLink>,{' '}
        <CsInkLink href="/random-coffee?ref=sched-case-study">Alfy Matching</CsInkLink>,{' '}
        <CsInkLink href="/kudos-recognition?ref=sched-case-study">Clappy Kudos</CsInkLink>,{' '}
        <CsInkLink href="/org-chart?ref=sched-case-study">Linky Directory</CsInkLink>,{' '}
        <CsInkLink href="/employee-survey?ref=sched-case-study">Pulsy Survey</CsInkLink> and{' '}
        <CsInkLink href="/standup-bot?ref=sched-case-study">Stany Standup</CsInkLink>. “Relatively
        easy but requires some maintenance for some apps.”
      </>,
      <>
        On the business: “More celebration of wins and positive feelings of appreciation for
        teamwork.” On the HR side: “Less time spent on manual processes. Better company morale.” And
        from the team: “Employees love it!”
      </>,
    ],
  },
  quotes: {
    challenge: {
      text: 'We wanted to build a culture intentionally; not just wing it. BuddiesHR helped us turn that intention into something we could actually follow through on.',
      highlight: 'build a culture intentionally',
    },
    result: {
      text: 'It allowed me to automate the processes and free up time while we still run effectively without an HR team.',
      highlight: 'without an HR team',
    },
  },
  interview: [
    [
      'The challenge',
      [
        [
          'What issues were you trying to fix when you found BuddiesHR?',
          'We wanted to reduce manual work we were already doing, increase communication and accountability, etc.',
        ],
        [
          'How did you notice these issues?',
          'We were spending a lot of time doing things manually that could be automated and now BuddiesHR does that for us.',
        ],
        [
          'What impact did it have on the business?',
          'It freed up my time and encourages the kind of behaviors and culture we want.',
        ],
        [
          'What was the trigger that made you search for a solution?',
          'We were spending more and more time on company culture things like birthday and work anniversaries for example.',
        ],
      ],
    ],
    [
      'Solution and implementation',
      [
        [
          'To fix the issues listed above, what were you looking for? What were the criteria?',
          '#1 Connected with Slack\n#2 Automated\n#3 Delegatable',
        ],
        [
          'The main reason that made you purchase?',
          '#1 Functionality was exactly what we wanted\n#2 Cost Efficient',
        ],
      ],
    ],
    [
      'Results',
      [
        [
          'Did BuddiesHR fix the problem described above? How?',
          'Yes! It allowed me to automate the processes and free up time while we still run effectively without an HR team.',
        ],
        [
          'How has the business changed after using BuddiesHR?',
          'More celebration of wins and positive feelings of appreciation for teamwork.',
        ],
        [
          'How was the ease of implementation? (how did it make their life easier?)',
          "Relatively easy but requires some maintenance for some apps (we're currently using Billy Birthday, Alfy Matching, Clappy Kudos, Linky Directory, Pulsy Survey and Stany Standup).",
        ],
        [
          'What impacts did it have on the HR team?',
          'Less time spent on manual processes. Better company morale.',
        ],
        ['Did you receive feedback from the employees?', 'Employees love it!'],
      ],
    ],
  ],
  closing: [
    'Sched’s experience with BuddiesHR is a powerful example of how even a fully remote team without a formal HR department can build a thriving, appreciative culture.',
    'By automating celebrations and recognition, Marvin and the Sched team created a scalable, fun, and values-driven approach to employee engagement.',
    'With apps like Billy Birthday, Clappy Kudos, and Pulsy Survey, Sched doesn’t just save time, it strengthens team morale, encourages positive behaviors, and ensures everyone feels seen and celebrated.',
    'The result? A happier team, a stronger culture, and more time for leadership to focus on what really matters.',
  ],
};
